import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../../../lib/supabase";

// --- GENİŞLETİLMİŞ TİPLER ---
interface FilterOptions {
  brands: Array<{ name: string; count: number }>;
  categories: string[];
}

// Hangi kategoride hangi filtreler görünecek? (Resimlerdeki tüm alanlar eklendi)
const FILTER_VISIBILITY: Record<string, string[]> = {
  erkek: [
    "category",
    "brand",
    "price",
    "size",
    "color",
    "fabric",
    "fit",
    "neckStyle",
    "sleeveLength",
    "sleeveType",
    "pocket",
    "pattern",
    "usageArea",
    "style",
  ],
  kadin: [
    "category",
    "brand",
    "price",
    "size",
    "color",
    "fabric",
    "fit",
    "neckStyle",
    "sleeveLength",
    "sleeveType",
    "pocket",
    "pattern",
    "usageArea",
    "style",
  ],
  elektronik: ["brand", "price", "ram", "storage", "color", "warranty"],
  telefon: ["brand", "price", "ram", "storage", "color", "os", "camera"],
};

// Resimlerdeki verilere göre sabit seçenekler
const FIXED_OPTIONS = {
  fabrics: [
    "%100 Pamuk",
    "3 İplik Şardonlu",
    "Denim",
    "Polyester",
    "Pamuk Karışımlı",
    "Triko",
    "Elastan",
  ],
  fits: ["Regular Fit", "Slim Fit", "Rahat Kalıp", "Oversize", "Skinny"],
  neckStyles: ["V Yaka", "Bisiklet Yaka", "Hakim Yaka", "Polo Yaka"],
  sleeveLengths: ["Kısa Kol", "Uzun Kol", "Sıfır Kol", "Truvakar Kol"],
  patterns: ["Düz", "Çizgili", "Ekose", "Baskılı", "Desenli"],
  usageAreas: ["Günlük", "Spor", "Klasik", "Outdoor"],
  styles: ["Trend", "Casual", "Classic", "Street Style"],
  pockets: ["Cepli", "Cepsiz"],
  sizes: ["L", "M", "XL", "2XL", "3XL", "XXS", "XS", "S", "XXL", "XXXL", "4XL"],
  colors: [
    { name: "Siyah", hex: "#000000" },
    { name: "Beyaz", hex: "#FFFFFF" },
    { name: "Lacivert", hex: "#000080" },
    { name: "Gri", hex: "#808080" },
    { name: "Kırmızı", hex: "#FF0000" },
    { name: "Mavi", hex: "#0000FF" },
  ],
};

export default function AdvancedFilters({
  categorySlug,
}: {
  categorySlug: string;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    brands: [],
    categories: [],
  });
  const [searchTerm, setSearchTerm] = useState({
    brand: "",
    size: "",
    fabric: "",
  });
  const [priceInput, setPriceInput] = useState({ min: "", max: "" });

  // Accordion (Açılır-Kapanır) yönetimi
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    category: true,
    brand: true,
    price: true,
    size: true,
    color: true,
  });

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const isVisible = (section: string) => {
    const active =
      FILTER_VISIBILITY[categorySlug] || FILTER_VISIBILITY["erkek"];
    return active.includes(section);
  };

  const updateParam = (key: string, value: string) => {
    const current = searchParams.get(key)?.split(",") || [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    next.length > 0
      ? searchParams.set(key, next.join(","))
      : searchParams.delete(key);
    setSearchParams(searchParams);
  };

  // Filtre Grubu Bileşeni (Resimdeki stile uygun)
  const FilterGroup = ({
    title,
    id,
    children,
    showSearch = false,
    searchKey = "",
  }: any) => {
    if (!isVisible(id)) return null;
    return (
      <div className="border-b border-gray-100 py-4">
        <button
          onClick={() => toggleSection(id)}
          className="flex items-center justify-between w-full text-left mb-2 group"
        >
          <span className="text-sm font-bold text-gray-700 uppercase tracking-tight">
            {title}
          </span>
          <i
            className={`ri-arrow-${openSections[id] ? "up" : "down"}-s-line text-gray-400 group-hover:text-gray-600 transition-transform`}
          ></i>
        </button>

        {openSections[id] && (
          <div className="space-y-2 pt-2 animate-fadeIn">
            {showSearch && (
              <div className="relative mb-3">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="Filtrele"
                  onChange={(e) =>
                    setSearchTerm((prev) => ({
                      ...prev,
                      [searchKey]: e.target.value,
                    }))
                  }
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-rose-300"
                />
              </div>
            )}
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 w-full max-w-[280px]">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest">
          Filtreleme
        </h2>
        {searchParams.toString() && (
          <button
            onClick={() => setSearchParams({})}
            className="text-rose-500 text-xs font-semibold hover:underline"
          >
            Sıfırla
          </button>
        )}
      </div>

      {/* 1. KATEGORİ (Resim 2'deki gibi) */}
      <FilterGroup title="Kategori" id="category">
        <div className="space-y-1 max-h-48 overflow-y-auto pr-2 custom-scrollbar text-sm text-gray-600">
          {[
            "Ceket",
            "Mont",
            "Hırka",
            "Alt Üst Takım",
            "Kaban & Parka",
            "Eşofman",
            "Kot Pantolon",
          ].map((cat) => (
            <div
              key={cat}
              className="py-1 hover:text-rose-500 cursor-pointer transition-colors"
            >
              {cat}
            </div>
          ))}
        </div>
      </FilterGroup>

      {/* 2. MARKALAR (Resim 2'deki gibi) */}
      <FilterGroup title="Marka" id="brand" showSearch searchKey="brand">
        <div className="space-y-2 max-h-40 overflow-y-auto pr-2 custom-scrollbar">
          {[
            "Çamaşır Kenti",
            "Tommy Hilfiger",
            "Cacharel",
            "U.S. Polo Assn.",
          ].map((brand) => (
            <label
              key={brand}
              className="flex items-center gap-2 group cursor-pointer"
            >
              <input
                type="checkbox"
                checked={searchParams.get("brands")?.includes(brand)}
                onChange={() => updateParam("brands", brand)}
                className="w-4 h-4 rounded border-gray-300 text-rose-500 focus:ring-rose-500"
              />
              <span className="text-sm text-gray-600 group-hover:text-gray-900">
                {brand}
              </span>
            </label>
          ))}
        </div>
      </FilterGroup>

      {/* 3. BEDEN (Resim 3'teki gibi) */}
      <FilterGroup title="Beden" id="size" showSearch searchKey="size">
        <div className="space-y-2">
          <p className="text-[10px] font-bold text-gray-400 flex items-center gap-1 uppercase">
            <i className="ri-fire-line text-orange-500"></i> Popüler Filtreler
          </p>
          {FIXED_OPTIONS.sizes.slice(0, 3).map((size) => (
            <label
              key={size}
              className="flex items-center gap-2 cursor-pointer"
            >
              <input
                type="checkbox"
                onChange={() => updateParam("sizes", size)}
                className="w-4 h-4 rounded border-gray-300 text-rose-500"
              />
              <span className="text-sm text-gray-600">{size}</span>
            </label>
          ))}
        </div>
      </FilterGroup>

      {/* 4. KUMAŞ TİPİ (Resim 4'teki gibi) */}
      <FilterGroup title="Kumaş Tipi" id="fabric" showSearch searchKey="fabric">
        <div className="space-y-2 max-h-40 overflow-y-auto pr-2 custom-scrollbar">
          {FIXED_OPTIONS.fabrics.map((f) => (
            <label key={f} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                onChange={() => updateParam("fabrics", f)}
                className="w-4 h-4 rounded border-gray-300 text-rose-500"
              />
              <span className="text-sm text-gray-600">{f}</span>
            </label>
          ))}
        </div>
      </FilterGroup>

      {/* 5. FİYAT ARALIĞI (Resim 4'teki gibi Manuel Girişli) */}
      <FilterGroup title="Fiyat Aralığı" id="price">
        <div className="flex items-center gap-2 mb-3">
          <input
            type="number"
            placeholder="En az"
            className="w-full px-2 py-2 border border-gray-200 rounded text-xs focus:border-rose-500 outline-none"
            onBlur={(e) => {
              searchParams.set("minPrice", e.target.value);
              setSearchParams(searchParams);
            }}
          />
          <input
            type="number"
            placeholder="En çok"
            className="w-full px-2 py-2 border border-gray-200 rounded text-xs focus:border-rose-500 outline-none"
            onBlur={(e) => {
              searchParams.set("maxPrice", e.target.value);
              setSearchParams(searchParams);
            }}
          />
          <button className="p-2 bg-gray-200 rounded hover:bg-gray-300">
            <i className="ri-search-line text-gray-500"></i>
          </button>
        </div>
      </FilterGroup>

      {/* 6. DİĞER DETAYLAR (Resim 5-6'daki gibi) */}
      <FilterGroup title="Kesim Kalıbı" id="fit">
        {FIXED_OPTIONS.fits.map((fit) => (
          <label
            key={fit}
            className="flex items-center gap-2 py-1 cursor-pointer"
          >
            <input
              type="checkbox"
              onChange={() => updateParam("fits", fit)}
              className="w-4 h-4 rounded border-gray-300 text-rose-500"
            />
            <span className="text-sm text-gray-600">{fit}</span>
          </label>
        ))}
      </FilterGroup>

      <FilterGroup title="Kol Boyu" id="sleeveLength">
        {FIXED_OPTIONS.sleeveLengths.map((sl) => (
          <label
            key={sl}
            className="flex items-center gap-2 py-1 cursor-pointer"
          >
            <input
              type="checkbox"
              onChange={() => updateParam("sleeveLength", sl)}
              className="w-4 h-4 rounded border-gray-300 text-rose-500"
            />
            <span className="text-sm text-gray-600">{sl}</span>
          </label>
        ))}
      </FilterGroup>

      {/* RENK (Renkli daireler) */}
      <FilterGroup title="Renk" id="color">
        <div className="grid grid-cols-6 gap-2">
          {FIXED_OPTIONS.colors.map((c) => (
            <button
              key={c.name}
              onClick={() => updateParam("colors", c.name)}
              className={`w-6 h-6 rounded-full border border-gray-200 transition-transform ${searchParams.get("colors")?.includes(c.name) ? "scale-125 border-rose-500 ring-2 ring-rose-200" : ""}`}
              style={{ backgroundColor: c.hex }}
              title={c.name}
            />
          ))}
        </div>
      </FilterGroup>
    </div>
  );
}
